import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaHome, FaEnvelope, FaCar, FaWrench, FaUsers, FaMoneyCheckAlt, FaChartBar, FaSignOutAlt, FaCog, FaTachometerAlt, FaInbox, FaUser, FaShoppingBag, FaChevronLeft, FaChevronRight, FaPlus, FaMoneyBillWave, FaTasks, FaProjectDiagram, FaColumns, FaTags, FaClipboardList, FaWallet, FaHandHoldingUsd, FaPercent, FaFileInvoiceDollar, FaSearch, FaBook, FaBalanceScale, FaCalendarAlt, FaDatabase, FaTrashAlt, FaCalculator, FaArchive, FaShieldAlt, FaQuestionCircle, FaMobileAlt, FaBox, FaBoxOpen, FaFileAlt, FaFolderOpen } from 'react-icons/fa';
import { Typography, List, ListItem, ListItemPrefix, Accordion, AccordionHeader, AccordionBody } from "@material-tailwind/react";
import {
    ChevronRightIcon,
    ChevronDownIcon,
} from "@heroicons/react/24/outline";

import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { rootUrl } from '../../config/config';
import { useAuth } from '../myTrack/AuthContext';
import { getExpenseSidebarSubItems } from '../myTrack/expenses/expenseNavigationConfig';
import { getCarTransactionsSummaryNavItem } from './carTransactionPermissions';
import { useInstallPrompt, promptInstall, requestInstallHelp } from '../../utils/installPrompt';

const menuItems = [
    { id: 'dashboard', label: 'Dashboard', Icon: FaHome, to: '/', permissionKey: 'dashboard.view' },
    {
        id: 'mpesa',
        label: 'M-Pesa',
        permissionKey: 'messages.view',
        Icon: FaMobileAlt,
        subItems: [
            { label: 'Inbox', to: '/messages', Icon: FaInbox },
            { label: 'Trans Messages', to: '/messages/trans', Icon: FaInbox },
            { label: 'Rules', to: '/messages/rules', Icon: FaCog },
            { label: 'C2B Transactions', to: '/payments/c2b-transactions', Icon: FaMobileAlt },
            { label: 'Webhook Logs', to: '/payments/webhook-logs', Icon: FaShieldAlt, permissionKey: 'payments.webhook_logs.view' },
        ]
    },
    {
        id: 'cars',
        label: 'Cars',
        permissionKey: 'vehicles.view',
        Icon: FaCar,
        subItems: [
            { label: 'Car List', to: '/cars', Icon: FaCar },
            { label: 'Insurance', to: '/insurance/fleet', Icon: FaShieldAlt },
            { label: 'Payments Per Car', to: '/cars/payments', Icon: FaMoneyBillWave },
            { label: 'Car Reports', to: '/report', Icon: FaChartBar },
            {
                label: 'Inspections',
                subItems: [
                    { label: 'Internal', to: '/inspections/internal', Icon: FaWrench },
                    { label: 'Government', to: '/inspections/government', Icon: FaWrench },
                ]
            },
            {
                label: 'Transactions',
                subItems: [
                    { label: 'Listing', to: '/all_transactions', Icon: FaShoppingBag },
                    { label: 'Add Transaction', to: '/transactions', Icon: FaPlus },
                    { label: 'Post Transaction', to: '/transactions/post', Icon: FaInbox },
                    { label: 'Bulk Posting', to: '/transactions/bulk', Icon: FaInbox },
                    { ...getCarTransactionsSummaryNavItem(), Icon: FaChartBar },
                    { label: 'Archived Trans', to: '/cars/deleted-transactions', Icon: FaTrashAlt, permissionKey: 'car.trans.view_deleted' },
                    { label: 'Archived Vehicles', to: '/cars/archived', Icon: FaArchive, permissionKey: 'roles.manage' },
                ]
            }
        ]
    },
    {
        id: 'drivers',
        label: 'Drivers',
        permissionKey: 'drivers.view',
        Icon: FaUser,
        subItems: [
            { label: 'Driver List', to: '/drivers', Icon: FaUser },
            { label: 'Blacklisted Drivers', to: '/drivers/blacklisted', Icon: FaUser, permissionKey: 'drivers.blacklist' },
            { label: 'Allocations', to: '/allocations', Icon: FaShoppingBag },
            {
                label: 'Transactions',
                subItems: [
                    { label: 'Listing', to: '/drivers/transactions', Icon: FaShoppingBag },
                    { label: 'Add Transaction', to: '/drivers/transactions/post', Icon: FaPlus },
                    { label: 'Bulk Posting', to: '/drivers/transactions/bulk', Icon: FaInbox },
                ]
            }
        ]
    },
    {
        id: 'inventory',
        label: 'Inventory',
        Icon: FaBox,
        subItems: [
            {
                label: 'Car Parts',
                Icon: FaBox,
                subItems: [
                    { label: 'Manage Parts', to: '/car_parts', Icon: FaBox },
                    { label: 'Post Parts Transaction', to: '/parts/transactions/post', Icon: FaInbox },
                    { label: 'Serial Lookup', to: '/parts/serials', Icon: FaSearch },
                    { label: 'Stock Valuation', to: '/reports/stock-valuation', Icon: FaBoxOpen, permissionKey: 'reports.stock.view' },
                ]
            },
            {
                label: 'Master Records',
                Icon: FaDatabase,
                subItems: [
                    { label: 'Part Models', to: '/parts/models', Icon: FaDatabase },
                    { label: 'Part Manufacturers', to: '/parts/manufacturers', Icon: FaUsers },
                    { label: 'Part Categories', to: '/parts/categories', Icon: FaTags },
                    { label: 'Part Statuses', to: '/parts/statuses', Icon: FaShieldAlt },
                    { label: 'Part Suppliers', to: '/parts/suppliers', Icon: FaShoppingBag },
                    { label: 'Units of Measure', to: '/parts/units', Icon: FaDatabase },
                    { label: 'Part Types', to: '/parts/types', Icon: FaMoneyCheckAlt },
                ]
            },
        ]
    },
    {
        id: 'owners',
        label: 'Owners',
        permissionKey: 'owners.view',
        Icon: FaUsers,
        subItems: [
            { label: 'Owner List', to: '/owners', Icon: FaUsers },
            {
                label: 'Transactions',
                subItems: [
                    { label: 'Listing', to: '/owners/transactions', Icon: FaShoppingBag },
                    { label: 'Post Transaction', to: '/owners/transactions/post', Icon: FaPlus },
                    { label: 'GL Posting', to: '/owners/gl-posting', Icon: FaBook },
                    { label: 'Payments', to: '/owners/payments', Icon: FaMoneyBillWave },
                ]
            },
            { label: 'Monthly Invoices', to: '/owners/invoices/monthly', Icon: FaFileInvoiceDollar },
            { label: 'Bulk Invoice by Cars', to: '/owners/invoices/monthly/bulk', Icon: FaFileInvoiceDollar, permissionKey: 'owner_invoice.edit' },
            { label: 'Monthly Reports', to: '/owners/invoices/monthly/reports', Icon: FaChartBar },
            { label: 'Zero Invoice Report', to: '/reports/zero-invoices', Icon: FaFileInvoiceDollar, permissionKey: 'reports.zero_invoices.view' },
            { label: 'Monthly Invoice Guide', to: '/owners/invoices/monthly/guide', Icon: FaQuestionCircle },
        ]
    },
    {
        id: 'vendors',
        label: 'Vendors',
        permissionKey: 'vendors.view',
        Icon: FaShoppingBag,
        subItems: [
            { label: 'Vendor Management', to: '/vendors', Icon: FaShoppingBag },
            { label: 'Vendor Ledgers', to: '/vendors/ledgers', Icon: FaMoneyCheckAlt },
        ]
    },
    {
        id: 'expenses',
        label: 'Expenses',
        permissionKey: 'expenses.view',
        Icon: FaMoneyBillWave,
        subItems: getExpenseSidebarSubItems()
    },
    {
        id: 'jobcards',
        label: 'Job Cards',
        permissionKey: 'jobcards.view',
        Icon: FaClipboardList,
        subItems: [
            { label: 'Job Cards List', to: '/job-cards/list', Icon: FaClipboardList },
            { label: 'Job Card Invoicing', to: '/job-cards-invoicing', Icon: FaFileInvoiceDollar },
            { label: 'Reconciliation', to: '/job-cards/reconciliation', Icon: FaSearch },
            { label: 'GL Posting', to: '/job-cards/gl-posting', Icon: FaBook }
        ]
    },
    {
        id: 'cashbook',
        label: 'Cashbook',
        permissionKey: 'cashbook.view',
        Icon: FaWallet,
        subItems: [
            { label: 'Add Transaction', to: '/cashbook/new', Icon: FaPlus },
            { label: 'Dashboard', to: '/cashbook', Icon: FaTachometerAlt, permissionKey: 'cashbook.dashboard.view' },
            { label: 'Accounts', to: '/cashbook/accounts', Icon: FaWallet },
            { label: 'Income Report', to: '/cashbook/report', Icon: FaChartBar },
        ]
    },
    {
        id: 'remittance',
        label: 'Remittance',
        permissionKey: 'remittance.view',
        Icon: FaHandHoldingUsd,
        subItems: [
            { label: 'Process Remittance', to: '/remittance/owner-report', Icon: FaChartBar },
            { label: 'Saved Reports', to: '/remittance/reports', Icon: FaFolderOpen },
            { label: 'Payment Reconciliation', to: '/remittance/reconciliation', Icon: FaSearch },
            { label: 'Remittance Summary Report', to: '/remittance/summary-report', Icon: FaChartBar },
            { label: 'Archived Remittances', to: '/remittance/archived', Icon: FaBook, permissionKey: 'remittance.view_archived' },
        ]
    },
    {
        id: 'finance',
        label: 'Finance',
        permissionKey: 'finance.view',
        Icon: FaMoneyCheckAlt,
        subItems: [
            {
                label: 'Ledger Settings',
                subItems: [
                    { label: 'Chart of Accounts', to: '/chart-of-accounts', Icon: FaChartBar },
                    { label: 'GL Master Data', to: '/gl-master-data', Icon: FaTags },
                    { label: 'Expense Categories', to: '/expenses/categories', Icon: FaTags },
                    { label: 'Expense Subcategories', to: '/expenses/subcategories', Icon: FaTags },
                ]
            },
            { label: 'GL Journals', to: '/gl-journals', Icon: FaBook, permissionKey: 'gl.post' },
            { label: 'Trial Balance', to: '/trial-balance', Icon: FaBalanceScale },
            { label: 'Profit and Loss', to: '/profit-and-loss', Icon: FaCalculator },
        ]
    },
    {
        id: 'tasks',
        label: 'Task Management',
        permissionKey: 'tasks.view',
        Icon: FaTasks,
        subItems: [
            { label: 'Workflows', to: '/workflows', Icon: FaProjectDiagram },
            { label: 'Task List', to: '/tasks', Icon: FaTasks },
            { label: 'Board View', to: '/tasks/board', Icon: FaColumns },
        ]
    },
    {
        id: 'reports',
        label: 'Reports',
        permissionKey: 'reports.view',
        Icon: FaChartBar,
        subItems: [
            { label: 'Reports Hub', to: '/reports', Icon: FaChartBar },
            { label: 'Weekly Reports', to: '/reports/weekly', Icon: FaCalendarAlt },
        ]
    },
    {
        id: 'admin',
        label: 'Administration',
        permissionKey: 'roles.manage',
        Icon: FaCog,
        subItems: [
            {
                label: 'System',
                subItems: [
                    { label: 'Settings', to: '/cosmosettings', Icon: FaCog },
                    { label: 'Logs', to: '/logs', Icon: FaInbox },
                    { label: 'Email Logs', to: '/email-logs', Icon: FaEnvelope },
                    { label: 'Login Audit', to: '/admin/login-audit', Icon: FaUser, permissionKey: 'roles.manage' },
                    { label: 'SQL Runner', to: '/admin/sql-runner', Icon: FaDatabase, permissionKey: 'roles.manage' },
                    { label: 'Week Management', to: '/admin/week-management', Icon: FaCalendarAlt },
                ]
            },
            {
                label: 'Master Data',
                subItems: [
                    { label: 'Car Status', to: '/cars/status', Icon: FaCar },
                    { label: 'Owner Statuses', to: '/owner/status', Icon: FaUsers },
                    { label: 'Insurance Companies', to: '/insurance/companies', Icon: FaUsers },
                    { label: 'Insurance Types', to: '/insurance/types', Icon: FaCog },
                    { label: 'Transaction Types', to: '/pay_trans/type', Icon: FaMoneyCheckAlt },
                    { label: 'Owner & Parts Types', to: '/settings/owner-payment-types', Icon: FaMoneyCheckAlt },
                    { label: 'Driver Statuses', to: '/settings/driver-statuses', Icon: FaTags },
                    { label: 'Document Types', to: '/settings/document-types', Icon: FaFileAlt },
                    { label: 'Inspection Settings', to: '/settings/inspections', Icon: FaWrench },
                    { label: 'VAT Rates', to: '/settings/vat-rates', Icon: FaPercent },
                    { label: 'Payers', to: '/settings/payers', Icon: FaUsers },
                    { label: 'Job Card Stages', to: '/settings/job-card-stages', Icon: FaTasks },
                    { label: 'Charge Types', to: '/job-cards/charge-types', Icon: FaTags },
                    { label: 'System Weeks', to: '/settings/system-weeks', Icon: FaCalendarAlt },
                    { label: 'Roles', to: '/admin/roles', Icon: FaUsers, permissionKey: 'roles.manage' },
                    { label: 'Permissions', to: '/admin/permissions', Icon: FaCog, permissionKey: 'roles.manage' },
                    { label: 'Users', to: '/admin/users', Icon: FaUsers, permissionKey: 'users.manage' },
                ]
            }
        ]
    },
    {
        id: 'logout',
        label: 'Logout',
        hasAction: 'logout',
        Icon: FaSignOutAlt,
    },
]

const taskWorkspaceShortcuts = [
    { id: 'workflows', label: 'Workflows', to: '/workflows', Icon: FaProjectDiagram },
    { id: 'tasks', label: 'Tasks', to: '/tasks', Icon: FaTasks },
    { id: 'board', label: 'Board', to: '/tasks/board', Icon: FaColumns },
];

const SideBarNav = ({ isAuthenticated, logout, toggleSidebar, isSidebarOpen, isCollapsed, setIsCollapsed }) => {
    const { token } = useAuth();
    const [perms, setPerms] = useState([]);
    const [appVersion, setAppVersion] = useState(null);
    const { canInstall, canPrompt } = useInstallPrompt();

    const handleInstallClick = () => {
        if (canPrompt) {
            promptInstall();
        } else {
            requestInstallHelp();
        }
    };

    useEffect(() => {
        if (!token) return;
        axios.get(`${rootUrl}/api/users/me/permissions`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => setPerms(r.data.data || []))
            .catch(() => setPerms([]));
    }, [token]);

    useEffect(() => {
        axios.get(`${rootUrl}/api/system/version`)
            .then(r => setAppVersion(r.data.data || null))
            .catch(() => {});
    }, []);

    const allowed = (item) => {
        // Always allow dashboard and logout to be visible
        if (item.id === 'dashboard' || item.hasAction === 'logout') return true;
        if (item.id === 'expenses') {
            return perms.includes(item.permissionKey) || hasVisibleItem(item, true);
        }
        if (!item.permissionKey) return true;
        return perms.includes(item.permissionKey);
    };

    const hasVisibleItem = (item, ignoreOwnPermission = false) => {
        if (!item) return false;
        if (!ignoreOwnPermission && item.permissionKey && !perms.includes(item.permissionKey)) return false;
        if (item.to) return true;
        if (item.subItems) return item.subItems.some((child) => hasVisibleItem(child));
        return false;
    };

    // Use Set to track multiple open accordions
    const [openItems, setOpenItems] = useState(new Set());
    const location = useLocation();
    const navRef = useRef(null);

    // Auto-collapse on mobile when sidebar is closed
    useEffect(() => {
        if (window.innerWidth < 768 && !isSidebarOpen) {
            setIsCollapsed(false);
        }
    }, [isSidebarOpen, setIsCollapsed]);

    const toggleCollapse = () => {
        setIsCollapsed(prev => !prev);
        // Close all accordions when collapsing
        if (!isCollapsed) {
            setOpenItems(new Set());
        }
    };

    const closeSidebar = () => {
        // Close sidebar on mobile when a link is clicked
        if (window.innerWidth < 768 && isSidebarOpen) {
            toggleSidebar();
        }
    };

    // Toggle accordion open/close state
    const handleOpen = (value) => {
        // Don't allow opening when collapsed
        if (isCollapsed) return;

        setOpenItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(value)) {
                newSet.delete(value);
            } else {
                newSet.add(value);
            }
            return newSet;
        });
    };

    const matchesPath = (currentPath, path) => {
        if (!path) return false;
        if (path === '/') return currentPath === '/';
        return currentPath === path || currentPath.startsWith(path + '/');
    };

    const findBestMatch = (items, currentPath, parentAccordionKeys = []) => {
        let bestMatch = null;

        items.forEach((item, index) => {
            if (item.permissionKey && !perms.includes(item.permissionKey)) return;

            if (item.to && matchesPath(currentPath, item.to)) {
                const candidate = {
                    path: item.to,
                    score: item.to.length,
                    openKeys: parentAccordionKeys,
                };

                if (!bestMatch || candidate.score > bestMatch.score) {
                    bestMatch = candidate;
                }
            }

            if (item.subItems) {
                const nestedAccordionKey = item.id
                    ? item.id
                    : parentAccordionKeys.length > 0
                        ? `${parentAccordionKeys[parentAccordionKeys.length - 1]}-${item.label}`
                        : item.label;

                const childMatch = findBestMatch(item.subItems, currentPath, [...parentAccordionKeys, nestedAccordionKey]);
                if (childMatch && (!bestMatch || childMatch.score > bestMatch.score)) {
                    bestMatch = childMatch;
                }
            }
        });

        return bestMatch;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const activeMatch = useMemo(
        () => findBestMatch(menuItems, location.pathname),
        [location.pathname, perms]
    );

    const activePath = activeMatch?.path || null;
    const isTaskWorkspaceRoute = activePath?.startsWith('/tasks') || activePath?.startsWith('/projects') || activePath?.startsWith('/workflows');

    // Check if route matches current best active path
    const isActiveRoute = (path) => {
        if (!path || !activePath) return false;
        return activePath === path;
    };

    // Check if an item is open in the accordion
    const isItemOpen = (value) => {
        return openItems.has(value);
    };

    // Check if any subItem or nested subItem matches current route
    const hasActiveChild = (subItems) => {
        if (!subItems) return false;
        return subItems.some(subItem => {
            if (subItem.to) {
                return isActiveRoute(subItem.to);
            }
            if (subItem.subItems) {
                return hasActiveChild(subItem.subItems);
            }
            return false;
        });
    };

    // Auto-expand parent menus when child is active (only when not collapsed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (isCollapsed) return;

        // Auto-expand menus that contain the active route
        const newOpenItems = new Set();
        menuItems.forEach(item => {
            if (item.subItems) {
                if (activeMatch?.openKeys?.includes(item.id) || hasActiveChild(item.subItems)) {
                    newOpenItems.add(item.id);
                }
                // Check nested subItems
                item.subItems.forEach(subItem => {
                    const nestedKey = `${item.id}-${subItem.label}`;
                    if (subItem.subItems && (activeMatch?.openKeys?.includes(nestedKey) || hasActiveChild(subItem.subItems))) {
                        newOpenItems.add(`${item.id}-${subItem.label}`);
                    }
                });
            }
        });
        setOpenItems(newOpenItems);
    }, [location.pathname, isCollapsed, activeMatch]);

    // Render nested submenu items (respect subItem permissionKey)
    const renderSubItems = (subItems, parentId = null, level = 0) => {
        if (isCollapsed) return null;

        return subItems.map((subItem, index) => {
            // Respect permissionKey on subItems
            if (subItem.permissionKey && !perms.includes(subItem.permissionKey)) return null;
            if (subItem.subItems && !hasVisibleItem(subItem)) return null;
            const uniqueKey = parentId ? `${parentId}-${subItem.label}-${index}` : `${subItem.label}-${index}`;
            const nestedId = parentId ? `${parentId}-${subItem.label}` : null;
            const isActive = subItem.to ? isActiveRoute(subItem.to) : false;
            const hasNestedActive = subItem.subItems ? hasActiveChild(subItem.subItems) : false;

            // If this subItem has nested subItems, render as nested accordion
            if (subItem.subItems) {
                const isNestedOpen = nestedId ? isItemOpen(nestedId) : false;
                return (
                    <div key={uniqueKey} className={`w-full ${level > 0 ? 'ml-3' : ''}`}>
                        <Accordion
                            open={isNestedOpen}
                            className="w-full"
                        >
                            <ListItem
                                className={`w-full p-0 mb-1 rounded-lg transition-colors duration-200 border ${isNestedOpen
                                    ? 'text-brand-primary font-medium border-gray-200 bg-gray-50'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-transparent hover:border-gray-200'
                                    }`}
                            >
                                <AccordionHeader
                                    onClick={() => nestedId && handleOpen(nestedId)}
                                    className="flex w-full min-w-0 items-center gap-x-1.5 rounded-lg border-b-0 pl-2 pr-0 py-1.5"
                                >
                                    <ListItemPrefix className="mr-0 flex-shrink-0">
                                        <ChevronRightIcon strokeWidth={3} className={`h-3 w-3 transition-transform duration-200 ${isNestedOpen || hasNestedActive ? 'text-brand-primary rotate-90' : 'text-gray-400'}`} />
                                    </ListItemPrefix>
                                    <Typography className="flex-1 min-w-0 truncate text-xs font-medium">
                                        {subItem.label}
                                    </Typography>
                                    <ChevronDownIcon
                                        strokeWidth={2.5}
                                        className={`mr-0 h-3 w-3 flex-shrink-0 text-gray-400 transition-transform duration-200 ${isNestedOpen ? 'rotate-180 text-brand-primary' : ''}`}
                                    />
                                </AccordionHeader>
                            </ListItem>
                            <AccordionBody className="py-1 px-0">
                                <List className="w-full min-w-0 max-w-full p-0 space-y-1">
                                    {renderSubItems(subItem.subItems, nestedId, level + 1)}
                                </List>
                            </AccordionBody>
                        </Accordion>
                    </div>
                );
            }

            // Regular subItem (no nesting)
            return (
                <ListItem
                    key={uniqueKey}
                    className={`w-full p-0 mb-1 rounded-lg transition-colors duration-200 group border ${isActive
                        ? 'bg-brand-primary/10 text-brand-primary font-medium border-brand-primary/20'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-transparent hover:border-gray-200'
                        } ${level > 0 ? 'ml-3' : ''}`}
                >
                    <Link
                        to={subItem.to}
                        onClick={closeSidebar}
                        className="flex items-center w-full px-2 py-1.5"
                    >
                        <ListItemPrefix className="mr-0 flex-shrink-0">
                            {subItem.Icon ? (
                                <subItem.Icon className={`h-3.5 w-3.5 ${isActive ? 'text-brand-primary' : 'text-gray-400 group-hover:text-gray-600'}`} />
                            ) : (
                                <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-200 ${isActive ? 'bg-brand-primary' : 'bg-gray-300 group-hover:bg-gray-400'}`} />
                            )}
                        </ListItemPrefix>
                        <Typography className="text-xs font-medium ml-1.5 leading-5">
                            {subItem.label}
                        </Typography>
                    </Link>
                </ListItem>
            );
        });
    };

    // Custom Tooltip Component for collapsed state
    const TooltipWrapper = ({ children, content, disabled = false }) => {
        const [showTooltip, setShowTooltip] = useState(false);

        if (disabled || !isCollapsed) {
            return <>{children}</>;
        }

        return (
            <div
                className="relative"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                {children}
                {showTooltip && (
                    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 pointer-events-none">
                        <div className="bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-xl whitespace-nowrap">
                            {content}
                            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <nav
            ref={navRef}
            className="sidebar-nav flex flex-col h-full w-full bg-white border-r border-gray-200"
        >
            {/* Collapse Toggle Button */}
            <div className="flex justify-end p-3 border-b border-gray-100">
                <TooltipWrapper content={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}>
                    <button
                        onClick={toggleCollapse}
                        className={`p-1.5 rounded-lg transition-colors duration-200 hover:bg-gray-100 text-gray-500 hover:text-gray-700 ${isCollapsed ? 'mx-auto' : 'ml-auto'
                            }`}
                        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {isCollapsed ? (
                            <FaChevronRight className="h-4 w-4" />
                        ) : (
                            <FaChevronLeft className="h-4 w-4" />
                        )}
                    </button>
                </TooltipWrapper>
            </div>

            {!isCollapsed && (
                <div className="px-3 pb-3 pt-3 border-b border-gray-100">
                    <div className="mb-3 flex items-center justify-between">
                        <div>
                            <Typography className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400">
                                Workspace Views
                            </Typography>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {taskWorkspaceShortcuts.map((item) => {
                            const active = isActiveRoute(item.to);
                            return (
                                <Link
                                    key={item.id}
                                    to={item.to}
                                    onClick={closeSidebar}
                                    className={`flex flex-col items-center justify-center gap-1 rounded-xl border px-2.5 py-2.5 text-[11px] font-semibold transition-all duration-200 ${active
                                        ? 'border-[#FF5733] bg-gradient-to-b from-[#fff8f4] to-[#fff0ea] text-[#FF5733] shadow-md ring-1 ring-[#FF5733]/10 -translate-y-0.5'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:-translate-y-0.5'
                                    }`}
                                >
                                    <span className={`flex h-6 w-6 items-center justify-center rounded-lg ${active ? 'bg-[#FF5733]/10' : 'bg-gray-50'}`}>
                                        <item.Icon className={`h-3.5 w-3.5 ${active ? 'text-[#FF5733]' : 'text-gray-400'}`} />
                                    </span>
                                    <span className="truncate leading-none">{item.label}</span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Scrollable Menu Container */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin">
                <div className={`pl-2.5 pr-0 py-4 transition-all duration-300 ${isCollapsed ? 'pl-1.5 pr-0' : ''}`}>
                    <List className="w-full min-w-0 max-w-full p-0 space-y-1">
                        {menuItems.map((item) => {
                            if (!allowed(item)) return null;
                            if (item.subItems && !hasVisibleItem(item)) return null;
                            if (!item.subItems) {
                                return (
                                    <TooltipWrapper key={item.id} content={item.label} disabled={!isCollapsed}>
                                        <ListItem
                                            className={`
                                                w-full
                                                ${item.hasAction === 'logout'
                                                    ? 'text-red-600 hover:bg-red-50 mt-auto border border-transparent hover:border-red-200'
                                                    : isActiveRoute(item.to)
                                                        ? 'bg-[#fff4ef] text-[#FF5733] font-medium border border-[#FF5733]/20'
                                                        : isTaskWorkspaceRoute && item.id === 'tasks'
                                                            ? 'bg-[#fffaf7] text-[#FF5733] font-medium border border-[#FF5733]/20'
                                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-transparent hover:border-gray-200'
                                                }
                                                rounded-lg mb-1 transition-colors duration-200 group
                                                ${isCollapsed ? 'justify-center px-1' : ''}
                                            `}

                                            onClick={() => {
                                                if (item.hasAction === 'logout') {
                                                    logout();
                                                }
                                            }}

                                        >
                                            <Link
                                                to={item.to}
                                                onClick={closeSidebar}
                                                className={`flex items-center w-full gap-x-1.5 ${isCollapsed ? 'justify-center px-0' : 'px-1.5'} py-2.5`}
                                            >
                                                <ListItemPrefix className={isCollapsed ? 'mr-0' : ''}>
                                                    <item.Icon className={`h-5 w-5 transition-transform duration-200 ${isActiveRoute(item.to) || (isTaskWorkspaceRoute && item.id === 'tasks') ? 'text-[#FF5733]' : 'text-gray-400 group-hover:text-gray-600'}`} />
                                                </ListItemPrefix>
                                                {!isCollapsed && (
                                                    <Typography className="mr-auto min-w-0 truncate text-xs font-medium">
                                                        {item.label}
                                                    </Typography>
                                                )}
                                            </Link>
                                        </ListItem>
                                    </TooltipWrapper>
                                )
                            }

                            return (
                                <Accordion
                                    key={item.id}
                                    open={!isCollapsed && isItemOpen(item.id)}
                                    className="w-full"
                                >
                                    <TooltipWrapper content={item.label} disabled={!isCollapsed}>
                                        <ListItem
                                            className={`w-full p-0 mb-1 rounded-lg transition-colors duration-200 group border ${isCollapsed ? 'justify-center' : ''} ${!isCollapsed && isItemOpen(item.id)
                                                ? 'bg-gray-50 text-brand-primary border-gray-200'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-transparent hover:border-gray-200'
                                                }`}
                                            selected={!isCollapsed && isItemOpen(item.id)}
                                        >
                                            <AccordionHeader
                                                onClick={() => !isCollapsed && handleOpen(item.id)}
                                                className={`flex w-full min-w-0 items-center gap-x-1.5 rounded-lg border-b-0 ${isCollapsed ? 'px-0 py-2 justify-center' : 'pl-1.5 pr-0 py-2'}`}
                                            >
                                                <ListItemPrefix className={`${isCollapsed ? 'mr-0' : 'mr-0'} flex-shrink-0`}>
                                                    <item.Icon className={`h-5 w-5 transition-transform duration-200 ${!isCollapsed && isItemOpen(item.id)
                                                        ? 'text-brand-primary'
                                                        : 'text-gray-400 group-hover:text-gray-600'
                                                        }`} />
                                                </ListItemPrefix>
                                                {!isCollapsed && (
                                                    <>
                                                        <Typography className="flex-1 min-w-0 truncate text-xs font-medium">
                                                            {item.label}
                                                        </Typography>
                                                        <ChevronDownIcon
                                                            strokeWidth={2.5}
                                                            className={`mr-0 h-3 w-3 flex-shrink-0 text-gray-400 transition-transform duration-200 ${isItemOpen(item.id) ? 'rotate-180 text-brand-primary' : ''}`}
                                                        />
                                                    </>
                                                )}
                                            </AccordionHeader>
                                        </ListItem>
                                    </TooltipWrapper>
                                    {!isCollapsed && (
                                        <AccordionBody className="py-1 px-0">
                                            <List className="w-full min-w-0 max-w-full p-0 space-y-0.5">
                                                {renderSubItems(item.subItems, item.id)}
                                            </List>
                                        </AccordionBody>
                                    )}
                                </Accordion>
                            )
                        })}
                    </List>
                </div>
            </div>
            {canInstall && (
                <div className="px-2 pt-2 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={handleInstallClick}
                        title="Install app"
                        className={`w-full flex items-center gap-2 rounded-lg py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 transition-colors ${isCollapsed ? 'justify-center px-0' : 'px-2'}`}
                    >
                        <FaMobileAlt className="h-3.5 w-3.5 flex-shrink-0" />
                        {!isCollapsed && <span>Install app</span>}
                    </button>
                </div>
            )}
            {appVersion && (
                <div className="px-3 py-2 border-t border-gray-100">
                    <div
                        className="group relative flex items-center justify-center cursor-default"
                        title={appVersion.released_at ? `Released ${appVersion.released_at}` : undefined}
                    >
                        <span className="text-xs text-gray-400 select-none">
                            {isCollapsed ? appVersion.version : `v${appVersion.version}`}
                        </span>
                        {!isCollapsed && appVersion.released_at && (
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50">
                                Released {appVersion.released_at}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default SideBarNav;
